import { FC, useMemo } from 'react';
import { format } from 'date-fns';

import { beautifyEnum, camelCaseToWords, capitalizeName } from 'src/utils/utility';
import IconWarning from 'src/assets/images/icons/warning-circle-grey.svg';
import IconInfo from 'src/assets/images/icons/warning-2.svg';
import IconCanceled from 'src/assets/images/icons/danger-light-main.svg';
import IconUploaded from 'src/assets/images/icons/document-text-grey.svg';
import IconFinished from 'src/assets/images/icons/tick-circle-light.svg';
import IconDeleted from 'src/assets/images/icons/trash-grey.svg';

import { IShipmentStatusProps } from './types';

export const LogShipmentStatus: FC<IShipmentStatusProps> = ({ log }) => {
  const statusIcon = useMemo(() => {
    switch (log?.actionOnObject) {
      case 'NEW_CHARGE_MAPPED_OTHER':
      case 'MAPPED_TO_RATE_CARD':
        return <img src={IconInfo} alt='' />;
      case 'REJECTED':
      case 'NEW_CHARGE_REJECTED':
        return <img src={IconCanceled} alt='' />;
      case 'DOCUMENT_UPLOADED':
        return <img src={IconUploaded} alt='' />;
      case 'DOCUMENT_DELETED':
        return <img src={IconDeleted} alt='' />;
      case 'APPROVED':
      case 'MARKED_READY_FOR_PAYMENT':
      case 'NEW_CHARGE_ACCEPTED_RECURRING':
      case 'NEW_CHARGE_ACCEPTED_ONE_OFF':
        return <img src={IconFinished} alt='' />;
      default:
        return <img src={IconWarning} alt='' />;
    }
  }, [log]);

  const title = useMemo(() => {
    switch (log?.actionOnObject) {
      case 'NEW_CHARGE_MAPPED_OTHER':
        return 'Unknown Charge Mapped to Rate Card';
      case 'NEW_CHARGE_ACCEPTED_RECURRING':
        return 'Accepted as Recurring';
      case 'NEW_CHARGE_REJECTED':
        return 'Unknown Charge Rejected';
      case 'NEW_CHARGE_ACCEPTED_ONE_OFF':
        return 'Accepted as One off';
      case 'DOCUMENT_UPLOADED':
        return 'New Charge Document Uploaded';
      case 'DOCUMENT_DELETED':
        return 'New Charge Document Deleted';
      case 'REJECTED':
        return 'Shipment Updated';
      case 'SENT_TO_PROCUREMENT':
        return 'Sent to Procurement Review';
      case 'SENT_TO_OPERATION':
        return 'Sent to Logistic Review';
      case 'APPROVED':
      case 'MARKED_READY_FOR_PAYMENT':
        return 'Shipment Updated';
      default:
        return beautifyEnum(log?.actionOnObject);
    }
  }, [log]);

  const meta: string[] = useMemo(() => {
    const items = [];

    if (log?.deviationActivity) {
      items.push(beautifyEnum(log?.deviationActivity?.chargeName));

      if (log?.actionOnObject === 'NEW_CHARGE_ACCEPTED_RECURRING') {
        items.push(`Recurring ${beautifyEnum(log?.deviationActivity?.deviationType)}`);
      } else if (log?.actionOnObject === 'NEW_CHARGE_MAPPED_OTHER') {
        items.push('Mapped to rate Card');
      }

      if (log.deviationActivity?.mappedChargeDomain) {
        const unitPrice = log.deviationActivity?.unitPrice ? ` - ${log.deviationActivity?.unitPrice} ${log.deviationActivity?.chargesCurrency ?? ''}` : '';
        items.push(`${beautifyEnum(log.deviationActivity?.mappedChargeDomain)}${unitPrice}`);
      }

      if (log.deviationActivity?.startDate && log.deviationActivity?.endDate) {
        items.push(
          `from ${format(new Date(log.deviationActivity?.startDate), 'dd-MMM-yyyy')} to ${format(new Date(log.deviationActivity?.endDate), 'dd-MMM-yyyy')}`
        );
      }
    }

    return items;
  }, [log]);

  const chargeSummary = useMemo(() => {
    if (!log?.shipmentOverview) return '';

    return (
      Object.entries(log.shipmentOverview)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([type, count]) => count > 0)
        .map(([type, count]) => `${count} ${camelCaseToWords(type)}`)
        .join(' - ')
    );
  }, [log.shipmentOverview]);

  return (
    <div className='flex gap-8 text-12 text-blue_grey-30 py-16 border-b border-neutral-20 first:pt-0 last:pb-0 last:border-0'>
      <div className='w-18'>{statusIcon}</div>
      <div className='flex-1'>
        <div className='flex items-center gap-16'>
          <div className='flex flex-col gap-6'>
            <h3 className='text-12 font-medium'>{title}</h3>
            <p className='text-10 text-blue_grey-30'>By {capitalizeName(log?.actionBy || 'System')}</p>
          </div>
          <div className='pl-16 ml-auto w-[100px] text-10 leading-14 text-blue_grey-30 text-right'>
            {format(new Date(log?.createdOn), 'dd-MMM-yyyy hh.mm.aaaa')} SGT
          </div>
        </div>

        {meta && meta.length > 0 && (
          <div className='mt-24 flex flex-col gap-4'>
            {meta.map((item) => (
              <div key={`meta-${item}`} className='text-12 text-blue_grey-30'>
                {item}
              </div>
            ))}
          </div>
        )}
        {chargeSummary !== '' && (
          <div className='mt-24 text-12'>
            <p>{chargeSummary}</p>
          </div>
        )}
        {log?.objectStatus === 'OBJECT_NO_CONTRACT_FOUND' && <div className='mt-24 text-12 text-blue_grey-30'>NRCF</div>}
      </div>
    </div>
  );
};
